import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEV_AGENTS, useAgentStore } from '../stores/agent';
import { Eye, EyeOff, X, ArrowRight, ShieldCheck, Lock, Zap, Server } from 'lucide-react';
import { AnimatedContent } from '../components/react-bits';
import { fetchOnboarding } from '../lib/onboarding-api';
import styles from './Login.module.css';

// One-click sign-in as seeded dev accounts
const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|::1|\[::1\])$/.test(window.location.hostname);
const showDemoAccounts = isLocalhost || import.meta.env.VITE_DEMO_LOGIN === '1';

export function Login() {
  const navigate = useNavigate();
  const login = useAgentStore((s) => s.login);
  const authMessage = useAgentStore((s) => s.authMessage);
  const clearAuthMessage = useAgentStore((s) => s.clearAuthMessage);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await signIn(email.trim(), password);
  }

  async function signIn(loginEmail: string, loginPassword: string) {
    if (submitting) return;
    setError(null);
    setUnverified(false);
    clearAuthMessage();
    setSubmitting(true);
    const result = await login(loginEmail, loginPassword);
    if (result.ok) {
      // A self-serve owner with setup left goes back to it; everyone else
      // (and anyone whose onboarding can't be read right now) to the dashboard.
      const onboarding = await fetchOnboarding().catch(() => null);
      navigate(onboarding?.nextStep ? '/onboarding' : '/dashboard');
      return;
    }
    setSubmitting(false);
    if (result.reason === 'unverified') {
      setUnverified(true);
      return;
    }
    setError(
      result.reason === 'invalid-credentials'
        ? 'Email or password is incorrect.'
        : result.reason === 'rate-limited'
          ? 'Too many failed sign-in attempts. Wait 15 minutes and try again.'
          : 'Could not reach the server. Is it running?',
    );
  }

  function signInAsDemo(devEmail: string) {
    setEmail(devEmail);
    setPassword('demo');
    void signIn(devEmail, 'demo');
  }

  return (
    <main className={styles.root}>
      {/* Studio Ambient Backdrop */}
      <div className={styles.studioGlow} aria-hidden="true" />

      {/* Studio Modal Card */}
      <AnimatedContent trigger="mount" distance={25} duration={0.65} ease="power3.out" className={styles.modalCard}>
        {/* ── Left Column: Form & Brand ── */}
        <div className={styles.formCol}>
          <div className={styles.topBrandRow}>
            <Link to="/" className={styles.brandPill} title="Openline Home">
              <span className={styles.brandPillDot} />
              <span>Openline</span>
            </Link>
          </div>

          <div className={styles.headerBlock}>
            <h1 className={styles.title}>Sign in to agent cockpit</h1>
            <p className={styles.subtitle}>
              Access real-time customer conversations &amp; live visitor telemetry
            </p>
          </div>

          {authMessage && !error && (
            <p className={styles.notice} role="status">
              {authMessage}
            </p>
          )}

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          {unverified && (
            <p className={styles.notice} role="alert">
              Confirm your email address first — open the link we sent you.{' '}
              <Link to={`/verify-email?email=${encodeURIComponent(email.trim())}`} className={styles.footerLink}>
                Send a new link
              </Link>
            </p>
          )}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="agent-email" className={styles.label}>
                Agent Email
              </label>
              <input
                id="agent-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="agent@yourcompany.internal"
                className={styles.input}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <label htmlFor="agent-password" className={styles.label}>
                  Password
                </label>
              </div>
              <div className={styles.passwordWrap}>
                <input
                  id="agent-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className={styles.input}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className={styles.submitBtn}
            >
              <span>{submitting ? 'Verifying credentials…' : 'Sign in to workstation'}</span>
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </form>

          {/* Quick Demo Sign-In */}
          {showDemoAccounts && (
            <div className={styles.demoSection}>
              <div className={styles.demoDivider}>
                <span>Quick dev sign-in</span>
              </div>
              <div className={styles.demoPills}>
                {DEV_AGENTS.map((agent) => (
                  <button
                    key={agent.email}
                    type="button"
                    onClick={() => signInAsDemo(agent.email)}
                    className={styles.demoPill}
                    title={`Sign in as ${agent.name}`}
                  >
                    <span className={`${styles.demoAvatar} ${styles[`demoAvatar_${agent.initials}`] || ''}`}>
                      {agent.initials}
                    </span>
                    <span>{agent.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className={styles.footerRow}>
            <Link to="/forgot-password" className={styles.footerLink}>
              Forgot password?
            </Link>
            <Link to="/signup" className={styles.footerLink}>
              Create an account
            </Link>
            <Link to="/" className={styles.footerLink}>
              Back to landing page
            </Link>
          </div>
        </div>

        {/* ── Right Column: Sovereign Workstation Showcase ── */}
        <div className={styles.visualCol}>
          <Link to="/" className={styles.closeBtn} title="Back to home" aria-label="Close login modal">
            <X size={18} />
          </Link>

          <div className={styles.showcaseHardwareFrame}>
            <div className={styles.showcaseTitlebar}>
              <div className={styles.showcaseDots}>
                <span className={`${styles.windowDot} ${styles.dotRed}`} />
                <span className={`${styles.windowDot} ${styles.dotYellow}`} />
                <span className={`${styles.windowDot} ${styles.dotGreen}`} />
              </div>
              <div className={styles.showcaseAddress}>
                <Lock size={11} className={styles.showcaseLockIcon} />
                <span>openline.internal · agent console</span>
              </div>
              <span className={styles.showcaseTelemetry}>
                <span className={styles.livePulseDot} /> &lt;12ms
              </span>
            </div>

            <div className={styles.showcaseImageWrap}>
              <img
                src="/landing/Openline-agent-01.png"
                alt="Openline sovereign live agent conversation workstation"
                className={styles.showcaseImg}
                loading="eager"
              />
            </div>
          </div>

          <div className={styles.showcaseBadges}>
            <div className={styles.showcaseBadgeItem}>
              <ShieldCheck size={14} className={styles.badgeIcon} />
              <span>Zero Cloud Telemetry</span>
            </div>
            <div className={styles.showcaseBadgeItem}>
              <Zap size={14} className={styles.badgeIcon} />
              <span>P2P WebRTC Direct Audio/Video</span>
            </div>
            <div className={styles.showcaseBadgeItem}>
              <Server size={14} className={styles.badgeIcon} />
              <span>Air-Gapped Sovereign Storage</span>
            </div>
          </div>
        </div>
      </AnimatedContent>
    </main>
  );
}
